'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useLiveQuery } from 'dexie-react-hooks';
import YarnDetailModal from '@/components/YarnDetailModal';
import YarnForm from '@/components/YarnForm';
import { MastheadAddButton } from '@/components/ui/MastheadAddButton';
import { ZineHeader } from '@/components/ui/ZineHeader';
import { BackBreadcrumb } from '@/components/ui/BackBreadcrumb';
import { db } from '@/lib/db';
import { log } from '@/lib/log';
import { softDelete, visible } from '@/lib/softDelete';

// Per-device flag tracking that the legacy yarn-reconcile pass has run
// at least once. After that the reconcile bails out, so phantom rows
// can't reappear post-deletion. Opaque to other tabs/devices on
// purpose — each device decides for itself whether it has done the
// initial backfill.
const YARN_RECONCILE_FLAG = 'knitlib.yarnReconcile.v1Done';
// Per-device flag for the one-time weight backfill (Veronika
// 2026-05-05). On the first My Yarns visit after upgrading, every
// row whose weight is empty / "I don't know" gets classified from
// its existing meters + grams + needle range. Real picks are never
// overwritten. Runs locally, syncs via Automerge as plain field
// updates, idempotent across devices.
//
// v2 (build 1033): the classifier was updated to use gauge stitches
// per 10 cm as the primary signal and to drop the m/100g vs needles
// "heavier wins" tiebreak. v2 uses the legacy classifier to detect
// rows that v1 wrote and re-classifies just those — manual picks
// stay untouched.
//
// v3 (build 1034): v2's "legacy" classifier accidentally used the
// new thresholds, so it didn't reproduce v1's actual output for
// European comma-decimal needle strings — Veronika's Como Tweed
// stayed at "Worsted" because v2 thought v1 wrote "Aran" and treated
// the row as a manual pick. v3 ships a true verbatim legacy
// classifier (old thresholds, comma-blind parser) and reruns the
// detection.
const WEIGHT_BACKFILL_FLAG = 'knitlib.weightBackfill.v3Done';
// Per-device flag for the one-time colorBucket backfill (Veronika
// 2026-05-05). Migrates existing yarns onto the new explicit-pick
// model: `isMulticolor=true` becomes `colorBucket='multicolor'`,
// otherwise the existing colorHex is classified into one of the 9
// color buckets. Yarns that already have a colorBucket set are
// untouched. Idempotent across devices via Automerge sync.
const COLOR_BUCKET_BACKFILL_FLAG = 'knitlib.colorBucketBackfill.v1Done';
// Build 1041 (Veronika 2026-05-05): pink became its own bucket. Any
// yarn the v1 backfill stuffed into 'red' whose hex now lands in the
// pink hue range gets rescued once. Manually-picked reds with
// genuinely red hexes stay where they are.
const COLOR_BUCKET_PINK_RESCUE_FLAG = 'knitlib.colorBucketPinkRescue.v1Done';
import { useIsMounted, splitZineTitle } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import type { Yarn } from '@/lib/types';
import { yarnMatchesLib, yarnMatchesLibWithFallback } from '@/lib/yarnIdentity';
import { bucketForHex, bucketForImage, type ColorBucket } from '@/lib/colorBucket';
import { classifyYarnWeight, classifyYarnWeightLegacy } from '@/lib/weightClassify';
import { blobRefHash, getBlobUrl, isBlobRef } from '@/lib/blobStore';
import { totalOwnedSkeins, claimedSkeins, uncountedPartialGrams } from '@/lib/skeinFormat';
import EmptyArchive from '@/components/redesign/EmptyArchive';
import PageSheepStrip from '@/components/redesign/PageSheepStrip';
import Modal from '@/components/Modal';
import { YarnBrowser } from '@/components/YarnBrowser';
import YarnRowCard from '@/components/YarnRowCard';

type SectionLabels = {
  white: string; yellow: string; orange: string; red: string; pink: string; violet: string;
  green: string; blue: string; brown: string; grey: string; black: string;
  multicolor: string; unknown: string;
  noBrand: string; noWeight: string;
};
type YarnSection = { key: string; label: string; yarns: Yarn[] };

const WEIGHT_ORDER = ['Lace', 'Fingering', 'Sport', 'DK', 'Worsted', 'Aran', 'Bulky', 'Super Bulky'];
const COLOR_ORDER: Array<ColorBucket | 'multicolor'> = [
  'white', 'yellow', 'orange', 'red', 'pink', 'violet', 'green', 'blue', 'brown', 'grey',
  'black', 'multicolor', 'unknown',
];

function buildSections(
  yarns: Yarn[],
  mode: 'color' | 'brand' | 'weight',
  bucketFor: (y: Yarn) => ColorBucket | 'multicolor',
  labels: SectionLabels,
): YarnSection[] {
  if (mode === 'color') {
    const colorLabels: Record<ColorBucket | 'multicolor', string> = {
      white: labels.white,
      yellow: labels.yellow,
      orange: labels.orange,
      red: labels.red,
      pink: labels.pink,
      violet: labels.violet,
      green: labels.green,
      blue: labels.blue,
      brown: labels.brown,
      grey: labels.grey,
      black: labels.black,
      multicolor: labels.multicolor,
      unknown: labels.unknown,
    };
    const buckets = new Map<string, Yarn[]>();
    for (const y of yarns) {
      const b = bucketFor(y);
      if (!buckets.has(b)) buckets.set(b, []);
      buckets.get(b)!.push(y);
    }
    return COLOR_ORDER
      .filter(k => buckets.has(k))
      .map(k => ({ key: k, label: colorLabels[k], yarns: buckets.get(k)! }));
  }
  if (mode === 'brand') {
    const groups = new Map<string, Yarn[]>();
    for (const y of yarns) {
      const key = (y.brand || '').trim() || '__no_brand__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(y);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        if (a === '__no_brand__') return 1;
        if (b === '__no_brand__') return -1;
        return a.localeCompare(b);
      })
      .map(([k, ys]) => ({
        key: k,
        label: k === '__no_brand__' ? labels.noBrand : k,
        yarns: ys,
      }));
  }
  // weight: canonical knit-weight order, then any custom strings
  // alphabetically, then the "no weight set" bucket last so unknowns
  // don't crowd the top.
  const groups = new Map<string, Yarn[]>();
  for (const y of yarns) {
    const w = (y.weight || '').trim();
    const key = !w || w === "I don't know" ? '__no_weight__' : w;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(y);
  }
  const orderedKnown = WEIGHT_ORDER.filter(w => groups.has(w));
  const customExtras = Array.from(groups.keys())
    .filter(k => k !== '__no_weight__' && !WEIGHT_ORDER.includes(k))
    .sort();
  const out: YarnSection[] = [];
  for (const k of [...orderedKnown, ...customExtras]) {
    out.push({ key: k, label: k, yarns: groups.get(k)! });
  }
  if (groups.has('__no_weight__')) {
    out.push({ key: '__no_weight__', label: labels.noWeight, yarns: groups.get('__no_weight__')! });
  }
  return out;
}

export default function YarnPage() {
  const router = useRouter();
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<Yarn | null>(null);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  // When set, the "Add Yarn" form opens pre-filled with all non-color
  // fields from an existing yarn (brand, fiber, weight, yardage, gauges,
  // etc.) so adding another colorway of a yarn you already own doesn't
  // make you retype the shared specs.
  const [prefillYarn, setPrefillYarn] = useState<Yarn | null>(null);
  // When set, the list drills into one yarn's colorways (flat view),
  // bypassing the grouped card layout so users can browse/edit a
  // specific colorway inside a group with many shades.
  const [groupFilter, setGroupFilter] = useState<{ name: string; brand: string } | null>(null);
  // Top-level sort/group mode for the yarn list. Veronika's spec
  // (2026-05-05): four buttons above the list — alphabet / color /
  // brand / weight. Alphabet keeps the existing collapsed-by-name
  // card view; the other three render section headings with all
  // matching yarns flat-listed underneath.
  const [sortMode, setSortMode] = useState<'alphabet' | 'color' | 'brand' | 'weight'>('alphabet');
  // BY COLOR drill-in: tapping a color card opens an alphabetical
  // list of just that color's yarns. Cleared automatically when the
  // user switches to a different sort mode.
  const [colorDrillIn, setColorDrillIn] = useState<ColorBucket | 'multicolor' | null>(null);
  // BY BRAND drill-in (Veronika 2026-05-05, build 1042): same UX as
  // BY COLOR but keyed by brand string. Empty-string sentinel for the
  // "no brand" bucket so the type system stays narrow.
  const [brandDrillIn, setBrandDrillIn] = useState<string | null>(null);
  // BY WEIGHT drill-in (Veronika 2026-05-05, build 1043): same UX
  // again. Empty-string sentinel = "weight not set" bucket.
  const [weightDrillIn, setWeightDrillIn] = useState<string | null>(null);
  // Cached dominant-color buckets derived from `colorPhoto` for yarns
  // that don't have a `colorHex` set. Keyed by the photo ref so the
  // result survives across yarn rows that share a photo and is reused
  // even if the underlying yarn row gets edited (the photo ref is
  // stable). One async derivation per photo per page visit.
  const [photoBuckets, setPhotoBuckets] = useState<Map<string, ColorBucket>>(new Map());
  const mounted = useIsMounted();
  const { t } = useI18n();

  // Stable cross-device sort — Dexie's `orderBy('name')` falls back to
  // the local auto-increment primary key when names tie, which differs
  // device-to-device (each device assigns its own ids locally, uuids
  // are the shared identity). That makes sync'd libraries show the
  // same rows in a different order on different devices — surprising
  // and ugly. Secondary sort on colorName / colorNumber gives an
  // alphabetical order inside each yarn name group that's identical
  // everywhere.
  const allYarns = useLiveQuery(async () => {
    const rows = visible(await db.yarns.orderBy('name').toArray());
    rows.sort((a, b) => {
      const byName = (a.name || '').localeCompare(b.name || '');
      if (byName !== 0) return byName;
      const byColorName = (a.colorName || '').localeCompare(b.colorName || '');
      if (byColorName !== 0) return byColorName;
      return (a.colorNumber || '').localeCompare(b.colorNumber || '');
    });
    return rows;
  });
  // Color resolution per yarn — priority order set by Veronika
  // Build 1040 (Veronika 2026-05-05): the user picks a colorBucket
  // explicitly from the 10-option palette. Auto-detection now only
  // runs as a fallback for legacy rows that haven't been classified
  // yet (the migration handles those on first My Yarns visit).
  //   1. explicit `colorBucket`        → use as-is (source of truth)
  //   2. legacy `isMulticolor` flag    → 'multicolor'
  //   3. cached photo bucket           → dominant-pixel HSL bucket
  //   4. parseable `colorHex`          → HSL bucket
  //   5. else                          → 'unknown'
  const bucketFor = (y: Yarn): ColorBucket | 'multicolor' => {
    if (y.colorBucket) return y.colorBucket;
    if (y.isMulticolor) return 'multicolor';
    if (y.colorPhoto) {
      const cached = photoBuckets.get(y.colorPhoto);
      if (cached) return cached;
      return 'unknown';
    }
    return bucketForHex(y.colorHex);
  };

  const yarns = (() => {
    if (!allYarns) return undefined;
    if (!search.trim()) return allYarns;
    const q = search.toLowerCase();
    return allYarns.filter(y =>
      y.name.toLowerCase().includes(q) ||
      y.brand?.toLowerCase().includes(q) ||
      y.fiber?.toLowerCase().includes(q) ||
      y.weight?.toLowerCase().includes(q) ||
      y.colorName?.toLowerCase().includes(q)
    );
  })();
  const projects = useLiveQuery(async () => visible(await db.projects.toArray()));

  // Reconcile: create missing yarn DB entries for colorways referenced
  // by projects but not present in the yarn table. Backfills entries
  // that were skipped by the old name-only matching logic. Modern
  // project saves (saveYarnsToDatabase in ProjectForm) handle this at
  // write time, so this exists only to repair pre-existing orphaned
  // refs from old data, share imports, and pre-Phase-25 schema rows.
  //
  // 2026-05-04 (Veronika): a no-color "phantom" Cardiff Cashmere kept
  // respawning every time she deleted it. The reconcile saw an
  // orphaned project ref ("Cardiff Cashmere classic" with empty
  // color), didn't find a matching colorway via the strict
  // yarnMatchesLib, and recreated the colorless row on every render.
  // Two-layer fix: (1) if a sibling yarn with the same name exists,
  // skip — never fragment the library with a colorless phantom; and
  // (2) gate the whole reconcile behind a per-device localStorage
  // flag, so it runs at most once per device. After the first run
  // any future orphans are left alone in the data layer; the user
  // can clean them up by editing the project's yarn ref directly.
  useEffect(() => {
    if (!allYarns || !projects) return;
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(YARN_RECONCILE_FLAG) === '1') return;
    (async () => {
      let createdAny = false;
      for (const p of projects) {
        for (const py of (p.yarns || [])) {
          if (!py.yarnName.trim()) continue;
          const exists = allYarns.find(y => yarnMatchesLib(py, y));
          if (exists) continue;
          const sibling = allYarns.find(y =>
            (y.name || '').trim().toLowerCase() === py.yarnName.trim().toLowerCase());
          if (sibling) continue;
          await db.yarns.add({
            name: py.yarnName.trim(),
            brand: py.brand || '',
            fiber: '',
            weight: '',
            colorName: py.colorName || '',
            colorNumber: py.colorNumber || '',
            colorHex: py.colorHex || '#B5654A',
            yardage: 0,
            skeinCount: 0,
            createdAt: new Date(),
          });
          createdAny = true;
        }
      }
      // Flag the device as reconciled regardless of whether anything
      // was actually created — once we've inspected the full set, the
      // device shouldn't keep retrying every time the user opens the
      // page. New phantom-worthy orphans coming in via sync will be
      // visible-but-orphaned until the user fixes the project ref.
      try { localStorage.setItem(YARN_RECONCILE_FLAG, '1'); } catch { /* private mode */ }
      if (createdAny) log.info('app', 'yarn-reconcile-ran');
    })();
  }, [allYarns?.length, projects?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time weight backfill — v2 (build 1033). Walks every yarn and
  // re-classifies under two conditions:
  //   1. Currently empty / "I don't know" → fill with new classifier
  //      (this is the case for devices that never ran the v1 pass).
  //   2. Currently matches what build 1030's legacy classifier would
  //      have produced for the same data → the value was written by
  //      auto-fill, not by the user, so it's safe to re-classify
  //      with the new gauge-first logic.
  // Anything else is treated as a manual pick and left alone.
  useEffect(() => {
    if (!allYarns) return;
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(WEIGHT_BACKFILL_FLAG) === '1') return;
    (async () => {
      let updated = 0;
      for (const y of allYarns) {
        if (!y.id) continue;
        const cur = (y.weight || '').trim();
        const isEmpty = !cur || cur.toLowerCase() === "i don't know";
        const legacyGuess = classifyYarnWeightLegacy({
          metersPerSkein: y.yardage || 0,
          gramsPerSkein: y.gramsPerSkein || 0,
          recommendedNeedles: y.recommendedNeedles,
        });
        const wasAutoFilled = isEmpty || (legacyGuess && cur === legacyGuess);
        if (!wasAutoFilled) continue;
        const newGuess = classifyYarnWeight({
          metersPerSkein: y.yardage || 0,
          gramsPerSkein: y.gramsPerSkein || 0,
          gaugeStitches: y.mfgGaugeStitches,
          gaugeStitchesMax: y.mfgGaugeStitchesMax,
          recommendedNeedles: y.recommendedNeedles,
        });
        const target = newGuess ?? "I don't know";
        if (cur === target) continue;
        await db.yarns.update(y.id, { weight: target });
        updated++;
      }
      try { localStorage.setItem(WEIGHT_BACKFILL_FLAG, '1'); } catch { /* private mode */ }
      if (updated > 0) log.info('app', 'weight-backfill-v3-ran', { updated });
    })();
  }, [allYarns?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time colorBucket migration (build 1040, Veronika 2026-05-05).
  // Move every legacy yarn onto the explicit-pick model so the new
  // BY COLOR grouping has something to read for every row. Rows that
  // already have a colorBucket set are skipped.
  useEffect(() => {
    if (!allYarns) return;
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(COLOR_BUCKET_BACKFILL_FLAG) === '1') return;
    (async () => {
      let updated = 0;
      for (const y of allYarns) {
        if (!y.id) continue;
        if (y.colorBucket) continue;
        let target: ColorBucket | 'multicolor' | null = null;
        if (y.isMulticolor) {
          target = 'multicolor';
        } else if (y.colorHex) {
          const guess = bucketForHex(y.colorHex);
          if (guess !== 'unknown') target = guess;
        }
        if (!target) continue;
        await db.yarns.update(y.id, { colorBucket: target });
        updated++;
      }
      try { localStorage.setItem(COLOR_BUCKET_BACKFILL_FLAG, '1'); } catch { /* private mode */ }
      if (updated > 0) log.info('app', 'color-bucket-backfill-v1-ran', { updated });
    })();
  }, [allYarns?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build 1041: rescue red→pink for yarns the v1 backfill misplaced
  // before pink had its own bucket. Only touches rows whose colorBucket
  // is 'red' AND whose colorHex now classifies as 'pink' under the
  // updated bucketForHex; everything else (manual picks, genuine reds)
  // is left alone.
  useEffect(() => {
    if (!allYarns) return;
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(COLOR_BUCKET_PINK_RESCUE_FLAG) === '1') return;
    (async () => {
      let updated = 0;
      for (const y of allYarns) {
        if (!y.id) continue;
        if (y.colorBucket !== 'red') continue;
        if (!y.colorHex) continue;
        if (bucketForHex(y.colorHex) !== 'pink') continue;
        await db.yarns.update(y.id, { colorBucket: 'pink' });
        updated++;
      }
      try { localStorage.setItem(COLOR_BUCKET_PINK_RESCUE_FLAG, '1'); } catch { /* private mode */ }
      if (updated > 0) log.info('app', 'color-bucket-pink-rescue-ran', { updated });
    })();
  }, [allYarns?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Photo-derived dominant color extraction. Runs for every non-
  // multicolor yarn that has a colorPhoto and hasn't been bucketed
  // yet — the photo is the *primary* color signal (Veronika 2026-05-05),
  // so we sample it regardless of whether colorHex parses. Each yarn's
  // colorPhoto is loaded into a 24×24 canvas and histogrammed; the
  // result is cached in state so a refilter is instant. We never block
  // the UI — yarns that are still pending bucketing simply don't
  // appear in any color filter until their bucket lands.
  useEffect(() => {
    if (!allYarns) return;
    const todo = allYarns.filter(y =>
      !y.isMulticolor &&
      y.colorPhoto &&
      !photoBuckets.has(y.colorPhoto),
    );
    if (todo.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = new Map<string, ColorBucket>();
      for (const y of todo) {
        if (cancelled) return;
        const ref = y.colorPhoto!;
        let url: string | null = null;
        if (isBlobRef(ref)) {
          url = await getBlobUrl(blobRefHash(ref));
        } else {
          url = ref;
        }
        if (!url) continue;
        const bucket = await new Promise<ColorBucket | null>(resolve => {
          const img = new Image();
          img.onload = () => resolve(bucketForImage(img));
          img.onerror = () => resolve(null);
          img.src = url;
        });
        if (bucket) updates.set(ref, bucket);
      }
      if (!cancelled && updates.size) {
        setPhotoBuckets(prev => {
          const next = new Map(prev);
          for (const [k, v] of updates) next.set(k, v);
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
  }, [allYarns, photoBuckets]);

  const getYarnStats = (yarn: Yarn) => {
    const owned = totalOwnedSkeins(yarn);
    if (!projects) return { inUse: 0, available: owned, rawAvailable: owned };
    const liveLibrary = (allYarns || []).filter(y => y.deletedAt == null);
    const matches = (y: { yarnName: string; colorName?: string; colorNumber?: string }) =>
      yarnMatchesLibWithFallback(y, yarn, liveLibrary);
    // Only active projects (in-progress / hibernating) reserve stock.
    // Finished projects already decremented the yarn when they were
    // marked finished — their skeins physically no longer exist, so
    // they don't affect the remaining-available count. Future
    // projects are plans, not commitments.
    // Sum whole + fractional skein usage. partialGramsUsed is only
    // counted when we know how many grams equal one skein; otherwise
    // we'd be unable to express it on the skein scale.
    const inUse = projects
      .filter(p => p.status === 'in-progress' || p.status === 'hibernating')
      .reduce((sum, p) => sum + (p.yarns || []).filter(matches).reduce(
        (s, y) => s + claimedSkeins(y, yarn.gramsPerSkein), 0), 0);
    const rawAvailable = owned - inUse;
    return { inUse, available: Math.max(0, rawAvailable), rawAvailable };
  };

  // Build 1045 (Veronika 2026-05-05): the "Review yarn" field is
  // yarn-line-shared — every row with the same (name, brand) keeps
  // the same review text. When any colorway saves, fan the review
  // out to all siblings. Latest write wins across the line.
  const propagateReviewToSiblings = async (
    name: string | undefined,
    brand: string | undefined,
    review: string | undefined,
    exceptId?: number,
  ) => {
    const trimmedName = (name || '').trim().toLowerCase();
    const trimmedBrand = (brand || '').trim().toLowerCase();
    if (!trimmedName) return;
    const all = await db.yarns.toArray();
    const siblings = all.filter(y =>
      y.deletedAt == null &&
      y.id != null &&
      y.id !== exceptId &&
      (y.name || '').trim().toLowerCase() === trimmedName &&
      (y.brand || '').trim().toLowerCase() === trimmedBrand,
    );
    for (const s of siblings) {
      if ((s.review || '') === (review || '')) continue;
      await db.yarns.update(s.id!, { review: review || undefined });
    }
  };

  const handleCreate = async (data: Omit<Yarn, 'id' | 'createdAt'>) => {
    // Duplicate guard: same yarn name + same color number but a
    // different color name is almost always a typo re-entry of an
    // existing row. Ask the user before silently creating a second
    // entry that will fragment their inventory. Only triggers when
    // BOTH sides have a color number — without one we can't tell
    // "this is the same yarn" from "this is a different colorway".
    const newNumber = (data.colorNumber || '').trim();
    const newName = (data.name || '').trim().toLowerCase();
    const newColor = (data.colorName || '').trim().toLowerCase();
    if (newNumber && newName) {
      const all = await db.yarns.toArray();
      const conflict = all.find(y =>
        y.deletedAt == null &&
        (y.name || '').trim().toLowerCase() === newName &&
        (y.colorNumber || '').trim() === newNumber &&
        (y.colorName || '').trim().toLowerCase() !== newColor,
      );
      if (conflict) {
        const useExisting = confirm(
          t.yarnDuplicatePrompt
            .replace('{name}', conflict.name)
            .replace('{color}', conflict.colorName || '')
            .replace('{number}', conflict.colorNumber || ''),
        );
        if (useExisting) {
          setShowNew(false);
          setSelected(conflict);
          return;
        }
      }
    }
    const newId = await db.yarns.add({ ...data, createdAt: new Date() } as Yarn);
    await propagateReviewToSiblings(data.name, data.brand, data.review, typeof newId === 'number' ? newId : undefined);
    setShowNew(false);
  };

  const handleEdit = async (data: Omit<Yarn, 'id' | 'createdAt'>) => {
    if (!selected?.id) return;
    await db.yarns.update(selected.id, data);
    await propagateReviewToSiblings(data.name, data.brand, data.review, selected.id);
    const updated = await db.yarns.get(selected.id);
    if (updated) setSelected(updated);
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!selected?.id) return;
    if (!confirm(t.deleteYarnConfirm)) return;
    await softDelete(db.yarns, selected.id);
    setSelected(null);
  };

  const openColorwayFormFrom = (source: Yarn) => {
    // Copy the shared specs from an existing colorway, blank out the
    // color-specific fields + stock + photo + notes, and open the
    // Add Yarn form pre-filled. Used from both the detail modal's
    // "Add another colorway" button and the grouped card's trailing
    // "+" button. The review is yarn-line-shared (build 1045), so
    // we copy it through — it stays in sync across all colorways.
    const shared: Yarn = {
      ...source,
      id: undefined,
      colorName: '',
      colorNumber: '',
      colorHex: '#D4A574',
      photo: undefined,
      skeinCount: 1,
      notes: undefined,
      createdAt: new Date(),
    };
    setPrefillYarn(shared);
    setSelected(null);
    setShowNew(true);
  };


  if (!mounted) return null;

  const { head: titleHead, tail: titleTail } = splitZineTitle(t.myYarnsTitle);

  return (
    <>
      {/* B1 zine masthead — 2026-05-09 build 1231 — Veronika 'when
          I open my yarns it also needs a new header'. Shared
          <ZineHeader> primitive with a '+' add button on the right. */}
      <ZineHeader
        head={titleHead}
        tail={titleTail}
        onBack={() => router.back()}
        right={<MastheadAddButton onClick={() => setShowNew(true)} />}
      />

      {/* Search — 2026-05-09 build 1246 — Veronika 'the search
          panel doesnt match our new design'. Restyled to B1 zine:
          1px ink border, paper2 fill, sharp corners, mono caps
          placeholder. */}
      {(allYarns?.length ?? 0) > 0 && (
        <div style={{ padding: '0 14px', marginBottom: 10 }}>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t.searchYarns}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'var(--paper-2)',
              border: '1px solid var(--ink)',
              borderRadius: 0,
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
              outline: 'none',
            }}
          />
        </div>
      )}

      {/* Sort-mode picker — 4 segments. Disabled while drilled into a
          colorway group, a single color, or actively searching, since
          all of those views render flat regardless of the chosen sort. */}
      {/* 2026-05-09 build 1237 — Veronika: B1 zine sort tabs —
          four equal-width bordered mono pills (NAME / COLOR /
          BRAND / WEIGHT), active fills black with paper text. */}
      {(allYarns?.length ?? 0) > 0 && !groupFilter && !colorDrillIn && !brandDrillIn && !weightDrillIn && !search.trim() && (
        <div style={{ padding: '0 14px', marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {([
              ['alphabet', t.byAlphabet],
              ['color', t.byColor],
              ['brand', t.byBrand],
              ['weight', t.byWeight],
            ] as const).map(([mode, label]) => {
              const active = sortMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setSortMode(mode); setColorDrillIn(null); setBrandDrillIn(null); setWeightDrillIn(null); }}
                  style={{
                    width: '100%',
                    padding: '7px 2px',
                    background: active ? 'var(--ink)' : 'transparent',
                    color: active ? 'var(--paper-1)' : 'var(--ink)',
                    border: '1px solid var(--ink)',
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    // 2026-05-09 build 1238 — Veronika: 'BY ALPHABET'
                    // was wrapping; smaller mono + tighter tracking +
                    // whiteSpace: nowrap keeps every label on one line
                    // while the buttons stay equal-width.
                    fontSize: 9,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    lineHeight: 1,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="px-5 space-y-3 pb-4">
        {yarns?.length === 0 && allYarns?.length !== 0 && (
          <p className="text-center text-xs text-text-tertiary py-8">{t.noMatches}</p>
        )}
        {allYarns?.length === 0 && (
          <EmptyArchive
            title={t.noYarnsYet}
            hint={t.yarnsAutoAdded}
            ctaLabel={t.addYarn}
            onAction={() => setShowNew(true)}
          />
        )}

        {/* Group-filter breadcrumb — shown when drilled into one colorway group */}
        {groupFilter && (
          <BackBreadcrumb label={t.allYarns} onClick={() => setGroupFilter(null)} />
        )}

        {/* Color-drill breadcrumb — back to the BY COLOR card grid */}
        {colorDrillIn && (
          <BackBreadcrumb label={t.allColors} onClick={() => setColorDrillIn(null)} />
        )}

        {/* Brand-drill breadcrumb — back to the BY BRAND card grid */}
        {brandDrillIn !== null && (
          <BackBreadcrumb label={t.allBrands} onClick={() => setBrandDrillIn(null)} />
        )}

        {/* Weight-drill breadcrumb — back to the BY WEIGHT card grid */}
        {weightDrillIn !== null && (
          <BackBreadcrumb label={t.allWeights} onClick={() => setWeightDrillIn(null)} />
        )}

        {(() => {
          if (!yarns) return null;
          // Drilled-in or searching → flat list. Otherwise the current
          // sortMode decides between (a) alphabet's grouped-by-name
          // collapsible cards and (b) section-headed sectional renders
          // for color / brand / weight.
          const isFlat = groupFilter != null || colorDrillIn != null || brandDrillIn != null || weightDrillIn != null || search.trim().length > 0;

          let flatYarns = yarns;
          if (groupFilter) {
            flatYarns = yarns.filter(y =>
              (y.name || '').toLowerCase() === groupFilter.name &&
              (y.brand || '').toLowerCase() === groupFilter.brand,
            );
          } else if (colorDrillIn) {
            flatYarns = yarns
              .filter(y => bucketFor(y) === colorDrillIn)
              .slice()
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          } else if (brandDrillIn !== null) {
            const wantsNoBrand = brandDrillIn === '';
            flatYarns = yarns
              .filter(y => {
                const b = (y.brand || '').trim();
                return wantsNoBrand ? b === '' : b === brandDrillIn;
              })
              .slice()
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          } else if (weightDrillIn !== null) {
            const wantsNoWeight = weightDrillIn === '';
            flatYarns = yarns
              .filter(y => {
                const w = (y.weight || '').trim();
                const isUnset = !w || w === "I don't know";
                return wantsNoWeight ? isUnset : w === weightDrillIn;
              })
              .slice()
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          }

          if (isFlat) {
            return flatYarns.map(yarn => {
              const s = getYarnStats(yarn);
              return (
                <YarnRowCard
                  key={yarn.id}
                  yarns={[yarn]}
                  totalAvailable={s.available}
                  totalInUse={s.inUse}
                  totalPartialGrams={uncountedPartialGrams(yarn)}
                  anyOver={s.rawAvailable < 0}
                  labels={{ available: t.available, inProgress: t.inUse, addColorway: t.addNewColorway }}
                  onCardClick={() => setSelected(yarn)}
                />
              );
            });
          }

          // Section-headed sort modes — each section gets a heading and
          // a flat list of every matching yarn underneath. BY COLOR
          // gets the special compact-card treatment Veronika asked for
          // 2026-05-05: one card per bucket, color name as the title,
          // every matching yarn as a mini swatch inside.
          if (sortMode === 'color' || sortMode === 'brand' || sortMode === 'weight') {
            const sections = buildSections(yarns, sortMode, bucketFor, {
              white: t.colorBucketWhite,
              yellow: t.colorBucketYellow,
              orange: t.colorBucketOrange,
              red: t.colorBucketRed,
              pink: t.colorBucketPink,
              violet: t.colorBucketViolet,
              green: t.colorBucketGreen,
              blue: t.colorBucketBlue,
              brown: t.colorBucketBrown,
              grey: t.colorBucketGrey,
              black: t.colorBucketBlack,
              multicolor: t.colorBucketMulticolor,
              unknown: t.colorBucketUnknown,
              noBrand: t.noBrandLabel,
              noWeight: t.noWeightLabel,
            });

            return (
              <YarnBrowser
                yarns={yarns}
                sortMode={sortMode}
                onSelectYarn={setSelected}
                getYarnStats={getYarnStats}
                sections={sections}
                bucketFor={bucketFor}
              />
            );
          }

          // Grouped view: bucket yarns by (name|brand), preserving
          // first-seen order so the list is stable under new additions.
          type Group = { key: string; name: string; brand: string; colorways: Yarn[] };
          const groupsByKey = new Map<string, Group>();
          const order: string[] = [];
          for (const y of yarns) {
            const key = `${(y.name || '').toLowerCase()}|${(y.brand || '').toLowerCase()}`;
            if (!groupsByKey.has(key)) {
              groupsByKey.set(key, {
                key,
                name: (y.name || '').toLowerCase(),
                brand: (y.brand || '').toLowerCase(),
                colorways: [],
              });
              order.push(key);
            }
            groupsByKey.get(key)!.colorways.push(y);
          }

          return order.map(key => {
            const g = groupsByKey.get(key)!;
            const stats = g.colorways.map(c => getYarnStats(c));
            const totalAvailable = stats.reduce((s, x) => s + x.available, 0);
            const totalInUse = stats.reduce((s, x) => s + x.inUse, 0);
            const totalPartialGrams = g.colorways.reduce((s, c) => s + uncountedPartialGrams(c), 0);
            const perYarnOver = stats.map(x => x.rawAvailable < 0);
            const anyOver = perYarnOver.some(Boolean);
            const multi = g.colorways.length > 1;
            return (
              <YarnRowCard
                key={multi ? g.key : g.colorways[0].id}
                yarns={g.colorways}
                totalAvailable={totalAvailable}
                totalInUse={totalInUse}
                totalPartialGrams={totalPartialGrams}
                anyOver={anyOver}
                perYarnOver={perYarnOver}
                labels={{ available: t.available, inProgress: t.inUse, addColorway: t.addNewColorway }}
                onCardClick={() =>
                  multi
                    ? setGroupFilter({ name: g.name, brand: g.brand })
                    : setSelected(g.colorways[0])
                }
                onColorwayClick={(c) => setSelected(c)}
                onAddColorway={() => openColorwayFormFrom(g.colorways[0])}
              />
            );
          });
        })()}
      </div>

      {/* Add new yarn — either a blank form or one pre-filled with the
           shared specs of an existing yarn (see prefillYarn). */}
      <Modal
        open={showNew}
        onClose={() => { setShowNew(false); setPrefillYarn(null); }}
        title={prefillYarn ? t.addNewColorwayTitle : t.addYarn}
      >
        <YarnForm
          key={prefillYarn?.id ?? 'blank'}
          initial={prefillYarn ?? undefined}
          onSubmit={async (data) => {
            await handleCreate(data);
            setPrefillYarn(null);
          }}
          onCancel={() => { setShowNew(false); setPrefillYarn(null); }}
        />
      </Modal>

      {/* Yarn detail modal — shared component */}
      {selected && !editing && (
        <YarnDetailModal
          yarn={selected}
          onClose={() => setSelected(null)}
          onEdit={() => setEditing(true)}
          onDelete={handleDelete}
          onDuplicateColorway={() => openColorwayFormFrom(selected)}
        />
      )}

      {/* Edit yarn modal */}
      {selected && editing && (
        <Modal open={true} onClose={() => setEditing(false)} title={`${t.edit} ${t.yarn}`}>
          <YarnForm initial={selected} onSubmit={handleEdit} onCancel={() => setEditing(false)} />
        </Modal>
      )}

      <PageSheepStrip />
    </>
  );
}

// YarnTile (the rich absolute-grid thumbnail for alphabet groups) has been
// extracted to src/components/YarnTile.tsx (build 1418) for reuse and clarity.
// The local alphabet path still uses a richer "row card" (big left tile +
// per-colorway over-allocation + add-colorway prefill) than the simpler
// YarnCard used by YarnBrowser for the sectioned modes. We keep the names
// distinct to avoid confusion.

